/**
 * Componente de configuração do gatilho webhook integrado para flows
 * Permite configurar modos Conversa ou Negócios com mapeamento de campos
 * 
 * CHANGELOG:
 *   RELEASE: 2026-09-07 v1.1.0 - Seleção de variável customizada alinhada ao modo destino.
 *   RELEASE: 2026-01-31 v1.0.1 - Adicionado suporte a ref imperativo para sincronização de variáveis.
 */

import { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { PhaseSelector } from '@/components/crm/PhaseSelector';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { 
  MessageSquare, 
  Briefcase, 
  Copy, 
  RefreshCw, 
  Info, 
  Link2,
  Phone,
  User,
  Mail,
  FileText,
  Tags,
  Variable,
  Plus,
  Trash2,
  Braces,
  Check,
  ChevronsUpDown,
  Layers
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { getEdgeFunctionUrl } from '@/lib/supabaseHelpers';
import { toast } from 'sonner';
import { WebhookTriggerConfig } from '@/types/flows';
import { VariableInsertButton } from '../VariableInsertButton';
import { LEAD_SOURCES, getLeadSourceIcon } from '@/lib/lead-sources';

interface FlowWebhookTriggerConfigProps {
  config: WebhookTriggerConfig;
  onChange: (config: WebhookTriggerConfig) => void;
  webhookToken: string | null;
  onRegenerateToken?: () => void;
  isRegenerating?: boolean;
}

/**
 * Interface para métodos expostos via ref imperativo
 * Permite que o componente pai (FlowWebhookDetailDialog) sincronize variáveis antes de salvar
 */
export interface FlowWebhookTriggerConfigRef {
  /** Sincroniza variáveis do estado local para o config global */
  syncVariables: () => void;
}

/**
 * Interface para entrada de variável com ID estável
 * Evita re-mount do componente durante digitação
 */
interface VariableEntry {
  id: string;      // ID estável para key do React
  name: string;    // Nome editável
  path: string;    // Caminho no payload
}

/**
 * Componente de configuração do webhook integrado
 * Utiliza forwardRef para expor métodos imperativos ao componente pai
 */
export const FlowWebhookTriggerConfig = forwardRef<
  FlowWebhookTriggerConfigRef,
  FlowWebhookTriggerConfigProps
>(function FlowWebhookTriggerConfig({
  config,
  onChange,
  webhookToken,
  onRegenerateToken,
  isRegenerating = false,
}, ref) {
  const { currentTenant } = useAuth();
  const [localConfig, setLocalConfig] = useState<WebhookTriggerConfig>(config);
  
  // =========================================================================
  // Estado local para edição fluida das variáveis (evita perda de foco)
  // Utiliza useRef para detectar mudanças reais no variable_mapping
  // =========================================================================
  const [variableEntries, setVariableEntries] = useState<VariableEntry[]>([]);

  // Estados dos popovers de Workspace e Pipeline (padrão CrmIntegrationSection)
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [pipelineOpen, setPipelineOpen] = useState(false);
  
  /**
   * Referência para armazenar o JSON do último variable_mapping carregado
   * Permite detectar mudanças reais quando o modal reabre com dados do banco
   */
  const lastLoadedMappingRef = useRef<string>('');

  // Sincroniza config local quando prop muda
  useEffect(() => {
    setLocalConfig(config);
  }, [config]);

  /**
   * Recarrega variableEntries quando config.variable_mapping REALMENTE muda
   * Corrige bug onde variáveis não carregavam ao reabrir modal
   * (componente não desmonta, apenas fica invisível)
   */
  useEffect(() => {
    const currentMappingJson = JSON.stringify(config.variable_mapping || {});
    
    // Só recarrega se o mapping mudou (evita loop infinito e mantém edições locais)
    if (currentMappingJson !== lastLoadedMappingRef.current) {
      const entries = Object.entries(config.variable_mapping || {}).map(
        ([name, path], index) => ({
          id: `var_${index}_${Date.now()}`,
          name,
          path: path as string,
        })
      );
      setVariableEntries(entries);
      lastLoadedMappingRef.current = currentMappingJson;
    }
  }, [config.variable_mapping]);

  // Atualiza config e propaga mudança
  const updateConfig = (updates: Partial<WebhookTriggerConfig>) => {
    const newConfig = { ...localConfig, ...updates };
    setLocalConfig(newConfig);
    onChange(newConfig);
  };

  // Atualiza field_mapping
  const updateFieldMapping = (field: string, value: string) => {
    updateConfig({
      field_mapping: {
        ...localConfig.field_mapping,
        [field]: value,
      },
    });
  };

  // =========================================================================
  // Funções de gerenciamento de variáveis customizadas do payload
  // Utiliza estado local para edição fluida e sincroniza no blur
  // =========================================================================

  /**
   * Adiciona uma nova variável ao mapeamento (estado local)
   */
  const addVariableMapping = () => {
    const existingCount = variableEntries.length;
    const newEntry: VariableEntry = {
      id: `var_${existingCount}_${Date.now()}`,
      name: `variavel_${existingCount + 1}`,
      path: '',
    };
    const newEntries = [...variableEntries, newEntry];
    setVariableEntries(newEntries);
    
    // Sincroniza imediatamente para o novo item aparecer
    syncVariablesToConfigFromEntries(newEntries);
  };

  /**
   * Atualiza o nome de uma variável LOCALMENTE (sem sincronizar)
   * Mantém o foco do input durante digitação
   */
  const updateLocalVariableName = (id: string, newName: string) => {
    setVariableEntries(prev =>
      prev.map(entry =>
        entry.id === id
          ? { 
              ...entry, 
              name: newName
                .toLowerCase()
                .replace(/\s+/g, '_')
                .replace(/[^a-z0-9_]/g, '') 
            }
          : entry
      )
    );
  };

  /**
   * Atualiza o caminho (path) de uma variável LOCALMENTE
   */
  const updateLocalVariablePath = (id: string, newPath: string) => {
    setVariableEntries(prev =>
      prev.map(entry =>
        entry.id === id ? { ...entry, path: newPath } : entry
      )
    );
  };

  /**
   * Sincroniza variáveis do estado local para o config global
   * Chamado no onBlur dos inputs e também via ref imperativo pelo componente pai
   */
  const syncVariablesToConfig = () => {
    syncVariablesToConfigFromEntries(variableEntries);
  };

  /**
   * Função auxiliar para sincronizar a partir de um array específico
   * Atualiza também a ref para evitar loop de recarregamento no useEffect
   */
  const syncVariablesToConfigFromEntries = (entries: VariableEntry[]) => {
    const mapping: Record<string, string> = {};
    entries.forEach(entry => {
      if (entry.name.trim()) {
        mapping[entry.name] = entry.path;
      }
    });
    
    // Atualiza ref ANTES de chamar updateConfig para evitar recarregamento
    lastLoadedMappingRef.current = JSON.stringify(mapping);
    updateConfig({ variable_mapping: mapping });
  };

  // =========================================================================
  // EXPOSIÇÃO DE MÉTODOS VIA REF IMPERATIVO
  // Permite que o componente pai chame syncVariables() antes de salvar
  // garantindo que variáveis editadas sejam incluídas mesmo sem onBlur
  // =========================================================================
  useImperativeHandle(ref, () => ({
    syncVariables: () => {
      syncVariablesToConfig();
    },
  }), [variableEntries]);

  /**
   * Remove uma variável do mapeamento (estado local + sincroniza)
   */
  const removeVariableMapping = (id: string) => {
    const newEntries = variableEntries.filter(entry => entry.id !== id);
    setVariableEntries(newEntries);
    syncVariablesToConfigFromEntries(newEntries);
  };

  // Buscar inboxes ativas
  const { data: inboxes } = useQuery({
    queryKey: ['inboxes', currentTenant?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('inboxes')
        .select('id, name, identifier, channel_type')
        .eq('tenant_id', currentTenant!.id)
        .eq('status', 'active')
        .order('name');
      
      if (error) throw error;
      return data;
    },
    enabled: !!currentTenant,
  });

  // Buscar workspaces CRM
  const { data: workspaces } = useQuery({
    queryKey: ['crm-workspaces', currentTenant?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('crm_workspaces')
        .select('id, name')
        .eq('tenant_id', currentTenant!.id)
        .eq('is_active', true)
        .order('name');
      
      if (error) throw error;
      return data;
    },
    enabled: !!currentTenant && localConfig.mode === 'deal',
  });

  // Buscar pipelines do workspace selecionado
  const { data: pipelines } = useQuery({
    queryKey: ['crm-pipelines', localConfig.workspace_id],
    queryFn: async () => {
      if (!localConfig.workspace_id) return [];
      
      const { data, error } = await supabase
        .from('crm_pipelines')
        .select('id, name')
        .eq('workspace_id', localConfig.workspace_id)
        .eq('is_active', true)
        .order('sort_order');
      
      if (error) throw error;
      return data;
    },
    enabled: !!localConfig.workspace_id,
  });

  // Buscar fases do pipeline selecionado
  const { data: phases } = useQuery({
    queryKey: ['crm-phases', localConfig.pipeline_id],
    queryFn: async () => {
      if (!localConfig.pipeline_id) return [];
      
      const { data, error } = await supabase
        .from('crm_pipeline_phases')
        .select('id, name, color')
        .eq('pipeline_id', localConfig.pipeline_id)
        .order('sort_order');
      
      if (error) throw error;
      return data;
    },
    enabled: !!localConfig.pipeline_id,
  });

  // URL do webhook
  // Encode do token para preservar caracteres especiais (+, =, etc.)
  const webhookUrl = webhookToken 
    ? `${getEdgeFunctionUrl('flow-webhook')}?token=${encodeURIComponent(webhookToken)}`
    : null;

  // Copiar URL
  const copyUrl = () => {
    if (webhookUrl) {
      navigator.clipboard.writeText(webhookUrl);
      toast.success('URL copiada!');
    }
  };

  return (
    <div className="space-y-4">
      {/* Seletor de Modo */}
      <div className="space-y-2">
        <Label>Modo de Operação</Label>
        <Tabs
          value={localConfig.mode}
          onValueChange={(v) => updateConfig({ mode: v as 'conversation' | 'deal' })}
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="conversation" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Conversa
            </TabsTrigger>
            <TabsTrigger value="deal" className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              Negócios
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <p className="text-xs text-muted-foreground">
          {localConfig.mode === 'conversation' 
            ? 'Cria ou reabre uma conversa para o contato e inicia o fluxo'
            : 'Cria um negócio no CRM para o contato e inicia o fluxo'
          }
        </p>
      </div>

      {/* Configuração específica por modo */}
      {localConfig.mode === 'conversation' && (
        <div className="space-y-2">
          <Label>Inbox Destino *</Label>
          <Select
            value={localConfig.inbox_id || ''}
            onValueChange={(v) => {
              const inbox = inboxes?.find((i) => i.id === v);
              updateConfig({ 
                inbox_id: v, 
                inbox_name: inbox?.name || '' 
              });
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione a inbox" />
            </SelectTrigger>
            <SelectContent>
              {inboxes?.map((inbox) => (
                <SelectItem key={inbox.id} value={inbox.id}>
                  {inbox.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!localConfig.inbox_id && (
            <p className="text-xs text-amber-600">⚠️ Selecione uma inbox</p>
          )}
        </div>
      )}

      {/* ===================================================================== */}
      {/* MODO NEGÓCIOS: Layout 2x2 com campos de destino CRM */}
      {/* ===================================================================== */}
      {localConfig.mode === 'deal' && (
        <div className="space-y-3">
          {/* Grid 2x2 padronizado com CrmIntegrationSection (Popover+Command + PhaseSelector) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Workspace - Popover + Command (padrão CrmIntegrationSection) */}
            <div className="space-y-2">
              <Label>Workspace CRM <span className="text-destructive">*</span></Label>
              <Popover open={workspaceOpen} onOpenChange={setWorkspaceOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={workspaceOpen}
                    className="w-full justify-between font-normal"
                  >
                    <div className="flex items-center gap-2 truncate">
                      <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className={cn("truncate", !localConfig.workspace_id && "text-muted-foreground")}>
                        {workspaces?.find((w) => w.id === localConfig.workspace_id)?.name || 'Selecione o workspace'}
                      </span>
                    </div>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0" align="start">
                  <Command>
                    <CommandList>
                      <CommandEmpty>Nenhum workspace encontrado.</CommandEmpty>
                      <CommandGroup>
                        {workspaces?.map((ws) => (
                          <CommandItem
                            key={ws.id}
                            value={ws.name}
                            onSelect={() => {
                              updateConfig({
                                workspace_id: ws.id,
                                workspace_name: ws.name,
                                pipeline_id: undefined,
                                pipeline_name: undefined,
                                phase_id: undefined,
                                phase_name: undefined,
                              });
                              setWorkspaceOpen(false);
                            }}
                          >
                            <div className="flex items-center gap-2 flex-1">
                              <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
                              <span className="flex-1 truncate">{ws.name}</span>
                            </div>
                            <Check
                              className={cn(
                                'ml-2 h-4 w-4',
                                localConfig.workspace_id === ws.id ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Pipeline - Popover + Command (padrão CrmIntegrationSection) */}
            <div className="space-y-2">
              <Label>Pipeline <span className="text-destructive">*</span></Label>
              {localConfig.workspace_id ? (
                <Popover open={pipelineOpen} onOpenChange={setPipelineOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={pipelineOpen}
                      className="w-full justify-between font-normal"
                    >
                      <div className="flex items-center gap-2 truncate">
                        <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className={cn("truncate", !localConfig.pipeline_id && "text-muted-foreground")}>
                          {pipelines?.find((p) => p.id === localConfig.pipeline_id)?.name || 'Selecione o pipeline'}
                        </span>
                      </div>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0" align="start">
                    <Command>
                      <CommandList>
                        <CommandEmpty>Nenhum pipeline encontrado.</CommandEmpty>
                        <CommandGroup>
                          {pipelines?.map((pl) => (
                            <CommandItem
                              key={pl.id}
                              value={pl.name}
                              onSelect={() => {
                                updateConfig({
                                  pipeline_id: pl.id,
                                  pipeline_name: pl.name,
                                  phase_id: undefined,
                                  phase_name: undefined,
                                });
                                setPipelineOpen(false);
                              }}
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
                                <span className="flex-1 truncate">{pl.name}</span>
                              </div>
                              <Check
                                className={cn(
                                  'ml-2 h-4 w-4',
                                  localConfig.pipeline_id === pl.id ? 'opacity-100' : 'opacity-0'
                                )}
                              />
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              ) : (
                <Button variant="outline" disabled className="w-full justify-between font-normal text-muted-foreground">
                  Selecione o workspace primeiro
                </Button>
              )}
            </div>

            {/* Fase Inicial - PhaseSelector reutilizável (padrão CrmIntegrationSection) */}
            <div className="space-y-2">
              <Label>Fase Inicial <span className="text-destructive">*</span></Label>
              {localConfig.pipeline_id ? (
                <PhaseSelector
                  pipelineId={localConfig.pipeline_id}
                  value={localConfig.phase_id || null}
                  onSelect={(v) => {
                    const ph = phases?.find((p) => p.id === v);
                    updateConfig({
                      phase_id: v,
                      phase_name: ph?.name || '',
                    });
                  }}
                  excludeFinalPhases={false}
                  placeholder="Selecione a fase"
                />
              ) : (
                <Button variant="outline" disabled className="w-full justify-between font-normal text-muted-foreground">
                  Selecione o pipeline primeiro
                </Button>
              )}
            </div>

            {/* Origem do Negócio - Select com LEAD_SOURCES (opcional) */}
            <div className="space-y-2">
              <Label>Origem do Negócio</Label>
              <Select
                value={localConfig.default_source || '__none__'}
                onValueChange={(v) => updateConfig({ 
                  default_source: v === '__none__' ? '' : v 
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione (opcional)..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Nenhuma</SelectItem>
                  {LEAD_SOURCES.map(source => {
                    const IconComponent = getLeadSourceIcon(source.id);
                    return (
                      <SelectItem key={source.id} value={source.id}>
                        <div className="flex items-center gap-2">
                          {IconComponent && <IconComponent className="h-4 w-4" />}
                          <span>{source.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Validação */}
          {(!localConfig.workspace_id || !localConfig.pipeline_id || !localConfig.phase_id) && (
            <p className="text-xs text-amber-600">
              ⚠️ Configure workspace, pipeline e fase para modo negócios
            </p>
          )}
        </div>
      )}

      {/* Mapeamento de Campos */}
      <div className="space-y-3 p-3 border rounded-lg bg-muted/30">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <Label>Mapeamento de Campos do Payload</Label>
        </div>
        
        <p className="text-xs text-muted-foreground">
          Informe o caminho dos campos no JSON do payload. Use dot notation para campos aninhados 
          (ex: <code className="bg-muted px-1 rounded">signatario.telefone</code>)
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Telefone (obrigatório) */}
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1">
              <Phone className="h-3 w-3" />
              Telefone *
            </Label>
            <Input
              placeholder="telefone"
              value={localConfig.field_mapping?.contact_phone || ''}
              onChange={(e) => updateFieldMapping('contact_phone', e.target.value)}
            />
          </div>

          {/* Nome */}
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1">
              <User className="h-3 w-3" />
              Nome
            </Label>
            <Input
              placeholder="nome"
              value={localConfig.field_mapping?.contact_name || ''}
              onChange={(e) => updateFieldMapping('contact_name', e.target.value)}
            />
          </div>

          {/* Email */}
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1">
              <Mail className="h-3 w-3" />
              Email
            </Label>
            <Input
              placeholder="email"
              value={localConfig.field_mapping?.contact_email || ''}
              onChange={(e) => updateFieldMapping('contact_email', e.target.value)}
            />
          </div>

          {/* Título do deal (apenas modo negócios) */}
          {localConfig.mode === 'deal' && (
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1">
                <Briefcase className="h-3 w-3" />
                Título do Negócio
              </Label>
              <Input
                placeholder="titulo"
                value={localConfig.field_mapping?.deal_title || ''}
                onChange={(e) => updateFieldMapping('deal_title', e.target.value)}
              />
            </div>
          )}

          {/* Tags do deal (apenas modo negócios) */}
          {localConfig.mode === 'deal' && (
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1">
                <Tags className="h-3 w-3" />
                Tags (array ou separadas por vírgula)
              </Label>
              <Input
                placeholder="tags"
                value={localConfig.field_mapping?.deal_tags || ''}
                onChange={(e) => updateFieldMapping('deal_tags', e.target.value)}
              />
            </div>
          )}
        </div>
      </div>

      {/* ================================================================= */}
      {/* Mapeamento de Variáveis Customizadas do Payload                   */}
      {/* ================================================================= */}
      <div className="space-y-3 p-3 border rounded-lg bg-purple-50/50 dark:bg-purple-900/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Variable className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            <Label className="text-purple-700 dark:text-purple-300">
              Variáveis Customizadas do Payload
            </Label>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addVariableMapping}
            className="h-7 text-xs"
          >
            <Plus className="h-3 w-3 mr-1" />
            Adicionar
          </Button>
        </div>
        
        <p className="text-xs text-muted-foreground">
          Mapeie campos do payload para variáveis que podem ser usadas nos nodes do fluxo 
          usando a sintaxe <code className="bg-muted px-1 rounded text-purple-600 dark:text-purple-400">{'{{nome_variavel}}'}</code>
        </p>

        {/* Lista de variáveis mapeadas */}
        {variableEntries.length === 0 ? (
          <div className="text-xs text-muted-foreground text-center py-2 border border-dashed rounded">
            Nenhuma variável customizada definida
          </div>
        ) : (
          <div className="space-y-2">
            {/* Cabeçalho das colunas */}
            <div className="grid grid-cols-[1fr_1fr_32px] gap-2 text-xs text-muted-foreground px-1">
              <span>Nome da Variável</span>
              <span>Caminho no Payload</span>
              <span></span>
            </div>
            
            {/* Lista de variáveis com key estável (ID) para evitar perda de foco */}
            {variableEntries.map((entry) => (
              <div key={entry.id} className="grid grid-cols-[1fr_1fr_32px] gap-2 items-center">
                {/* Campo nome com botão de seleção de variáveis existentes */}
                <div className="relative">
                  <Input
                    value={entry.name}
                    onChange={(e) => updateLocalVariableName(entry.id, e.target.value)}
                    onBlur={syncVariablesToConfig}
                    placeholder="nome_variavel"
                    className="h-8 text-sm font-mono pr-9"
                  />
                  {/* Ícone {} para selecionar variável existente */}
                  <div className="absolute right-1 top-1/2 -translate-y-1/2">
                    <VariableInsertButton
                      tenantId={currentTenant?.id || ''}
                      excludeSystem={true}
                      mode="destination"
                      onSelectVariable={(variable) => {
                        updateLocalVariableName(entry.id, variable.name);
                        syncVariablesToConfig();
                      }}
                      variant="icon"
                      className="h-6 w-6"
                    />
                  </div>
                </div>
                <Input
                  value={entry.path}
                  onChange={(e) => updateLocalVariablePath(entry.id, e.target.value)}
                  onBlur={syncVariablesToConfig}
                  placeholder="caminho.no.payload"
                  className="h-8 text-sm font-mono"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeVariableMapping(entry.id)}
                  className="h-8 w-8"
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Dica de uso - usa variableEntries para exibir em tempo real */}
        {variableEntries.length > 0 && (
          <Alert className="bg-purple-50/50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
            <Info className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            <AlertDescription className="text-xs text-purple-700 dark:text-purple-300">
              Use as variáveis nos nodes de texto: {variableEntries.filter(e => e.name.trim()).slice(0, 3).map(entry => (
                <code key={entry.id} className="bg-purple-100 dark:bg-purple-800 px-1 mx-0.5 rounded">{`{{${entry.name}}}`}</code>
              ))}
              {variableEntries.filter(e => e.name.trim()).length > 3 && '...'}
            </AlertDescription>
          </Alert>
        )}
      </div>

      {webhookToken && (
        <div className="space-y-2 p-3 border rounded-lg bg-primary/5">
          <div className="flex items-center gap-2">
            <Link2 className="h-4 w-4 text-primary" />
            <Label className="text-primary">URL do Webhook</Label>
            <Badge variant="outline" className="ml-auto">
              {localConfig.mode === 'conversation' ? 'Conversa' : 'Negócios'}
            </Badge>
          </div>
          
          <div className="flex gap-2">
            <Input
              readOnly
              value={webhookUrl || ''}
              className="font-mono text-xs"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={copyUrl}
              title="Copiar URL"
            >
              <Copy className="h-4 w-4" />
            </Button>
            {onRegenerateToken && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={onRegenerateToken}
                disabled={isRegenerating}
                title="Regenerar token"
              >
                <RefreshCw className={`h-4 w-4 ${isRegenerating ? 'animate-spin' : ''}`} />
              </Button>
            )}
          </div>
          
          <Alert className="bg-background">
            <Info className="h-4 w-4" />
            <AlertDescription className="text-xs">
              Envie uma requisição POST para esta URL com o payload JSON contendo os campos mapeados.
              O token será invalidado se regenerado.
            </AlertDescription>
          </Alert>
        </div>
      )}

      {!webhookToken && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Salve o gatilho para gerar a URL do webhook.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
});
